import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Fab,
  Snackbar,
  Alert,
} from '@mui/material';
import { Phone, Warning } from '@mui/icons-material';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * EmergencyOverlay - Always-visible emergency contact buttons during app lock.
 *
 * CRITICAL: These buttons must ALWAYS work even when device is pinned.
 * Uses Intent.ACTION_DIAL (not ACTION_CALL) so the user confirms the call.
 * Android allows DIAL intent even in lock task mode.
 *
 * Emergency contacts are loaded from:
 * families/{familyId}/emergencyContacts/
 */
function EmergencyOverlay({ familyId, isVisible = true }) {
  const [momPhone, setMomPhone] = useState(null);
  const [dadPhone, setDadPhone] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!familyId) return;
    loadEmergencyContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyId]);

  const loadEmergencyContacts = async () => {
    try {
      const momRef = doc(db, `families/${familyId}/emergencyContacts/mom`);
      const dadRef = doc(db, `families/${familyId}/emergencyContacts/dad`);

      const [momSnap, dadSnap] = await Promise.all([
        getDoc(momRef),
        getDoc(dadRef),
      ]);

      if (momSnap.exists()) setMomPhone(momSnap.data().phone);
      if (dadSnap.exists()) setDadPhone(dadSnap.data().phone);
    } catch (err) {
      console.error('EmergencyOverlay: Failed to load contacts:', err);
    }
  };

  const callNumber = (number, label) => {
    try {
      // Use tel: link which works in WebView and opens dialer
      // Android allows this even in lock task (pinned) mode
      window.location.href = `tel:${number}`;
      setSnackbar({ open: true, message: `Opening dialer for ${label}...` });
    } catch (err) {
      console.error('Call failed:', err);
      setSnackbar({ open: true, message: `Could not open dialer. Call ${number}` });
    }
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Floating emergency button - always visible */}
      {!expanded && (
        <Fab
          color="error"
          size="medium"
          onClick={() => setExpanded(true)}
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 16,
            zIndex: 9999,
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%': { boxShadow: '0 0 0 0 rgba(211, 47, 47, 0.4)' },
              '70%': { boxShadow: '0 0 0 10px rgba(211, 47, 47, 0)' },
              '100%': { boxShadow: '0 0 0 0 rgba(211, 47, 47, 0)' },
            },
          }}
          aria-label="Emergency contacts"
        >
          <Warning />
        </Fab>
      )}

      {/* Expanded emergency panel */}
      {expanded && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 70,
            right: 8,
            left: 8,
            zIndex: 9999,
            p: 2,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #b71c1c 0%, #d32f2f 100%)',
            color: 'white',
            border: '2px solid #ff5252',
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ color: 'white' }}>
              🚨 Emergency Contacts
            </Typography>
            <Button
              size="small"
              onClick={() => setExpanded(false)}
              sx={{ color: 'white', minWidth: 'auto', p: 0.5 }}
            >
              ✕
            </Button>
          </Box>

          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', display: 'block', mb: 1.5 }}>
            Always available, even during screen lock
          </Typography>

          {/* 911 Button */}
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<Phone />}
            onClick={() => callNumber('911', '911 Emergency')}
            sx={{
              mb: 1.5,
              py: 1.5,
              backgroundColor: '#fff',
              color: '#b71c1c',
              fontWeight: 'bold',
              fontSize: '1.1rem',
              '&:hover': { backgroundColor: '#ffcdd2' },
            }}
          >
            🚨 Call 911 - Emergency
          </Button>

          {/* Dad Button */}
          {dadPhone && (
            <Button
              fullWidth
              variant="outlined"
              size="large"
              startIcon={<Phone />}
              onClick={() => callNumber(dadPhone, 'Dad')}
              sx={{
                mb: 1,
                py: 1.2,
                borderColor: 'white',
                color: 'white',
                fontWeight: 'bold',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)', borderColor: 'white' },
              }}
            >
              👨 Call Dad
            </Button>
          )}

          {/* Mom Button */}
          {momPhone && (
            <Button
              fullWidth
              variant="outlined"
              size="large"
              startIcon={<Phone />}
              onClick={() => callNumber(momPhone, 'Mom')}
              sx={{
                py: 1.2,
                borderColor: 'white',
                color: 'white',
                fontWeight: 'bold',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)', borderColor: 'white' },
              }}
            >
              👩 Call Mom
            </Button>
          )}

          {!dadPhone && !momPhone && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', textAlign: 'center', mt: 1 }}>
              Parent contacts not set up yet
            </Typography>
          )}
        </Paper>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="info" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default EmergencyOverlay;
